import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { Icon } from '../Icon';
import type { ILookupProps } from './ILookupProps';
import { PickerList } from './PickerList';
import { useLookup } from './useLookup';
import './pickers.css';

/**
 * Комбобокс пошуку людей і записів (UIUX/controls/lookup; прототип fieldVM user): поле 34px з аватаром 22px вибраної особи,
 * введенням, × очищення й шевроном; список у порталі (fixed, до 300px, вгору біля низу вікна) з підвалом. Пошук з першого символу,
 * Enter — перший збіг. ARIA: role=combobox, aria-expanded, aria-controls, aria-activedescendant.
 */
export function Lookup(props: ILookupProps) {
  const { t } = useTranslation();
  const s = useLookup(props);
  const { value, text, initials, readOnly, size = 'md' } = props;
  // У клітинці таблиці (inline) — лише текст, як у прототипі: без аватара, × і шеврона.
  const compact = size === 'inline';
  const showAvatar = !compact && !s.open && Boolean(value) && Boolean(initials);
  return (
    <div ref={s.box} className={cx('lk', `lk--${size}`, s.open && 'is-open', readOnly && 'is-ro', props.invalid && 'is-invalid')}
      onMouseDown={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); s.input.current?.focus(); } }}>
      {props.prefix && <span className="pk__prefix">{props.prefix}</span>}
      {showAvatar && <span className="lk__avatar" aria-hidden>{initials}</span>}
      <input ref={s.input} id={props.id} className="lk__input" role="combobox" aria-label={props.label} aria-expanded={s.open}
        aria-controls={s.open ? s.listId : undefined} aria-autocomplete="list" aria-activedescendant={s.open && s.hi >= 0 ? `${s.listId}-o${s.hi}` : undefined}
        aria-invalid={props.invalid || undefined} readOnly={readOnly} disabled={props.disabled} autoFocus={props.autoFocus} autoComplete="off" spellCheck={false}
        value={s.open ? s.query ?? '' : text} placeholder={props.placeholder ?? t('fieldControls.typeName')} title={text || undefined}
        onFocus={s.start} onClick={s.start} onChange={(e) => s.type(e.target.value)} onKeyDown={s.onKeyDown} onBlur={s.onBlur} />
      {!compact && !readOnly && !props.disabled && value && !s.open && (
        <button type="button" className="lk__clear" aria-label={t('fieldControls.clear')} title={t('fieldControls.clear')}
          onMouseDown={(e) => e.preventDefault()} onClick={() => s.pick('')}>
          <Icon name="x" size={13} strokeWidth={2.2} />
        </button>
      )}
      {!compact && !readOnly && <Icon name="chevron-down" size={14} strokeWidth={2} className="lk__chev" />}
      {s.open && s.pos && (
        <PickerList listId={s.listId} panelRef={s.panel} pos={s.pos} label={props.label} options={props.options} value={value} hi={s.hi}
          onPick={(o) => s.pick(o.value)} onHover={s.setHi} search={null} loading={props.loading} footer={props.footer}
          emptyText={props.emptyText ?? t('fieldControls.noMatches')} className="pk-list--lookup" />
      )}
    </div>
  );
}
